<script lang="ts" setup>
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'

const props = defineProps<{ name: string, id: string }>()
const emit = defineEmits(['close'])
const schema = z.object({
  name: z.string().min(3, 'Must be at least 3 characters'),
  email: z.string().email('Invalid email'),
  phone: z.string().min(10, 'Enter a valid phone number'),
  message: z.string().min(10, 'Must be at least 10 characters'),
})

type Schema = z.output<typeof schema>

const message = reactive({
  name: '',
  email: '',
  phone: '',
  message: '',
})

const uiFormGroup = { label: { base: 'text-blue' } }
const { validateTurnstileToken, token } = useTurnstile()
async function sendMessage(event: FormSubmitEvent<Schema>) {
  const pb = useNuxtApp().$pb
  const toast = useToast()
  toast.add({ title: 'Sending message', icon: 'ri:mail-line', id: 'sending-message' })
  try {
    await validateTurnstileToken(token.value)
    await pb.send(`/business/send-message/${props.id}`, { method: 'POST', body: event.data })
    toast.remove('sending-message')
    toast.add({ title: 'Message sent', icon: 'ri:check-line', id: 'message-sent' })
    emit('close')
  }
  catch (err: any) {
    toast.remove('sending-message')
    toast.add({ title: 'Failed to send message', icon: 'ri:error-warning-line', id: 'failed-message', color: 'red', description: err.message })
  }
}
</script>

<template>
  <div>
    <div class="p-4 md:p-8 lg:p-12 bg-gray-50">
      <HeadingText
        tag="h4"
        class="pb-6 text-yellow"
      >
        Send message to {{ name }}
      </HeadingText>
      <UForm
        :state="message"
        class="flex gap-2 flex-col"
        :schema="schema"
        @submit.prevent="sendMessage"
      >
        <UFormGroup
          label="Name"
          required
          size="lg"
          :ui="uiFormGroup"
          name="name"
        >
          <UInput
            v-model="message.name"
            :ui="{ form: '!bg-white' }"
          />
        </UFormGroup>
        <UFormGroup
          label="Email"
          required
          size="lg"
          :ui="uiFormGroup"
          name="email"
        >
          <UInput
            v-model="message.email"
            type="email"
            :ui="{ form: '!bg-white' }"
          />
        </UFormGroup>
        <UFormGroup
          label="Phone"
          required
          size="lg"
          :ui="uiFormGroup"
          name="phone"
        >
          <UInput
            v-model="message.phone"
            type="tel"
            :ui="{ form: '!bg-white' }"
          />
        </UFormGroup>
        <UFormGroup
          label="Message"
          required
          size="lg"
          :ui="uiFormGroup"
          name="message"
        >
          <UTextarea
            v-model="message.message"
            :ui="{ rounded: 'rounded-3xl', form: '!bg-white' }"
          />
        </UFormGroup>
        <NuxtTurnstile v-model="token" />
        <UButton
          size="xl"
          icon="ri:mail-line"
          class="self-end mt-6"
          color="pink"
          type="submit"
        >
          Send
        </UButton>
      </UForm>
    </div>
  </div>
</template>
